// components/BrokerHelpPopup.js
import { useState, useEffect } from "react";
import { HelpCircle, Send, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import PortalFeedback from "./PortalFeedback";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

export default function BrokerHelpPopup({
  className = "",
  panelClass = PANEL,
  open: controlledOpen,
  onOpenChange,
  renderButton,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successText, setSuccessText] = useState("");
  const [errorText, setErrorText] = useState("");

  // auto-fill email
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (nextOpen) => {
    if (onOpenChange) onOpenChange(nextOpen);
    else setUncontrolledOpen(nextOpen);
    if (nextOpen) {
      setSuccessText("");
      setErrorText("");
    }
  };

  useEffect(() => {
    (async () => {
      const { data: au } = await supabase.auth.getUser();
      if (au?.user?.email) {
        setEmail(au.user.email);
      }
    })();
  }, []);

  async function sendHelpRequest() {
    setErrorText("");
    setSuccessText("");

    if (!subject.trim() || !message.trim()) {
      setErrorText("Add a subject and message before sending.");
      return;
    }

    setLoading(true);

    const { data, error } = await supabase.functions.invoke("send-help-email", {
      body: {
        from: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      },
    });

    setLoading(false);

    if (error || data?.error) {
      console.error(error || data?.error);
      const detail = data?.error || error?.context?.error || error?.message;
      setErrorText(
        detail?.includes("RESEND_API_KEY")
          ? "Help email is not configured yet. Add the Resend API key in Supabase secrets, then try again."
          : detail || "Could not send the help request. Please try again."
      );
      return;
    }

    setSuccessText("Strobe Aviation has received your help request and will email you back ASAP.");
    setSubject("");
    setMessage("");
  }

  return (
    <>
      {/* Help Button */}
      {renderButton ? (
        renderButton({ open: () => setOpen(true) })
      ) : (
        <button
          onClick={() => setOpen(true)}
          className={`${panelClass} ${className} p-2 hover:bg-white/20 transition inline-flex items-center justify-center`}
          aria-label="Help"
          title="Need help?"
        >
          <HelpCircle className="w-5 h-5 text-[#1A1A1A]" />
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />

          <div className="relative w-full max-w-lg rounded-2xl border border-black/10 bg-white shadow-2xl">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 p-1 rounded hover:bg-black/5"
              aria-label="Close help request"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="border-b border-black/10 px-5 py-4 pr-12">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-[#EB040D]" />
                <h2 className="text-lg font-semibold">Need Help?</h2>
              </div>
              <p className="mt-1 text-sm text-black/60">
                Send a note to Strobe support. We’ll reply to the email on your account.
              </p>
            </div>

	            <div className="space-y-4 p-5">
	              {successText && (
	                <PortalFeedback type="success">{successText}</PortalFeedback>
	              )}
	              {errorText && (
	                <PortalFeedback type="error">
	                  {errorText}
	                </PortalFeedback>
	              )}

              {/* User email (read-only) */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your Email
                </label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full rounded-md border border-black/10 bg-gray-100 px-3 py-2 text-sm"
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  placeholder="Brief summary"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Message
                </label>
                <textarea
                  rows={4}
                  placeholder="Describe your issue or question…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-sm rounded-md border border-black/10 hover:bg-black/[0.04]"
                >
                  {successText ? "Close" : "Cancel"}
                </button>
                <button
                  onClick={sendHelpRequest}
                  disabled={loading || !!successText}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-[#EB040D] text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {loading ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
